import { useEffect } from "react";

/**
 * Keep the screen awake while a session is live. The host reads a card,
 * puts the phone down while the room performs, picks it back up — without
 * this the phone locks mid-task and the party stalls on a lock screen.
 *
 * Best-effort like haptics: silently does nothing where the Screen Wake
 * Lock API is missing (old iOS Safari). Re-acquires on tab return because
 * the browser auto-releases the lock when the page is hidden.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let disposed = false;

    const acquire = async () => {
      try {
        const s = await navigator.wakeLock.request("screen");
        if (disposed) {
          s.release().catch(() => {});
          return;
        }
        sentinel = s;
      } catch {
        // Denied (battery saver, background tab) — non-fatal, retry on next visibility.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [active]);
}
